/**
 * Carrousel de témoignages, avec défilement automatique.
 *
 * S'appuie sur le défilement natif avec scroll-snap plutôt que sur une
 * bibliothèque : sans JavaScript la piste reste utilisable à la main, et on
 * n'ajoute rien au bundle.
 *
 * Le nombre de pages est déduit de la largeur RÉELLE des cartes, jamais codé en
 * dur : la mise en page passe d'une carte par vue sous 768px à deux au-delà, et
 * un nombre figé de puces serait faux sur l'un des deux cas.
 *
 * Contrat de balisage (cf. views/partials/components/cards/testimonial.twig) :
 *  - [data-testimonial-slider] : le conteneur
 *  - [data-slider-track]       : la piste défilante
 *  - [data-slider-slide]       : chaque carte
 *  - [data-slider-dots]        : le conteneur des puces
 */

const DOT_BASE = 'testimonial-dot';
const AUTOPLAY_DELAY = 6000; // ms entre deux pages
const RESUME_DELAY = 10000; // reprise après une action de l'internaute

export function initTestimonialSlider(context = document) {
  const sliders = context.querySelectorAll('[data-testimonial-slider]');
  if (!sliders.length) return;

  sliders.forEach((slider) => {
    if (slider.__testimonialSlider) return; // garde contre la double init
    slider.__testimonialSlider = true;

    const track = slider.querySelector('[data-slider-track]');
    const dotsHolder = slider.querySelector('[data-slider-dots]');
    const slides = Array.from(slider.querySelectorAll('[data-slider-slide]'));

    if (!track || !dotsHolder || slides.length === 0) return;

    let pages = 0;
    let timer = null;
    let resumeTimer = null;
    let hovered = false;
    let visible = true;

    /* ── Géométrie ───────────────────────────────────────────────────────── */

    const perView = () => {
      const slideWidth = slides[0].getBoundingClientRect().width;
      if (!slideWidth) return 1;
      // Arrondi : la largeur inclut la gouttière, un ratio brut donnerait 1,9.
      return Math.max(1, Math.round(track.clientWidth / slideWidth));
    };

    const currentPage = () =>
      Math.min(
        Math.max(pages - 1, 0),
        Math.max(0, Math.round(track.scrollLeft / track.clientWidth))
      );

    const goToPage = (index) => {
      track.scrollTo({
        left: index * track.clientWidth,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    };

    /* ── Défilement automatique ──────────────────────────────────────────── */

    const canAutoplay = () => pages > 1 && !prefersReducedMotion();

    const stopAutoplay = () => {
      window.clearInterval(timer);
      timer = null;
    };

    const startAutoplay = () => {
      stopAutoplay();
      if (!canAutoplay()) return;

      timer = window.setInterval(() => {
        // On ne fait rien tant que le carrousel est survolé, hors écran, ou
        // que l'onglet est en arrière-plan : avancer sans témoin serait du
        // travail inutile, et repartir sous le curseur est désagréable.
        if (hovered || !visible || document.hidden) return;
        goToPage((currentPage() + 1) % pages);
      }, AUTOPLAY_DELAY);
    };

    /**
     * Suspend la reprise après une action de l'internaute : cliquer sur une
     * puce puis voir le carrousel avancer une seconde plus tard donne
     * l'impression que le clic a été ignoré.
     */
    const deferAutoplay = () => {
      stopAutoplay();
      window.clearTimeout(resumeTimer);
      resumeTimer = window.setTimeout(startAutoplay, RESUME_DELAY);
    };

    /* ── Puces ───────────────────────────────────────────────────────────── */

    const buildDots = () => {
      const count = Math.ceil(slides.length / perView());

      if (count === pages) return;
      pages = count;
      dotsHolder.innerHTML = '';

      // Une seule page : les puces n'apporteraient rien.
      if (count < 2) {
        dotsHolder.hidden = true;
        stopAutoplay();
        return;
      }

      dotsHolder.hidden = false;

      for (let i = 0; i < count; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = DOT_BASE;
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-label', `Page ${i + 1} sur ${count}`);
        dot.addEventListener('click', () => {
          goToPage(i);
          deferAutoplay();
        });
        dotsHolder.appendChild(dot);
      }

      syncActiveDot();
      startAutoplay();
    };

    const syncActiveDot = () => {
      if (pages < 2) return;
      const index = currentPage();
      Array.from(dotsHolder.children).forEach((dot, i) => {
        const active = i === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    };

    /* ── Écoutes ─────────────────────────────────────────────────────────── */

    let ticking = false;
    track.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => {
          syncActiveDot();
          ticking = false;
        });
      },
      { passive: true }
    );

    // Défilement à la main : on laisse la main à l'internaute.
    track.addEventListener('pointerdown', deferAutoplay, { passive: true });
    track.addEventListener('touchstart', deferAutoplay, { passive: true });

    slider.addEventListener('mouseenter', () => {
      hovered = true;
    });
    slider.addEventListener('mouseleave', () => {
      hovered = false;
    });

    // Navigation au clavier dans le carrousel : même logique que le survol.
    slider.addEventListener('focusin', () => {
      hovered = true;
    });
    slider.addEventListener('focusout', () => {
      hovered = false;
    });

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) syncActiveDot();
    });

    // N'anime que si la section est à l'écran.
    if (typeof IntersectionObserver !== 'undefined') {
      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            visible = entry.isIntersecting;
          });
        },
        { threshold: 0.2 }
      ).observe(slider);
    }

    let resizeTimer = null;
    window.addEventListener('resize', () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(buildDots, 150);
    });

    buildDots();
  });
}

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
